import { UseQueryOptions } from '@tanstack/react-query';
import {
	scopedClientKey,
	ScopedPermissionOwnershipTransfer,
	ScopedPermissionsConfig,
	scopedProjectKey,
	ScopeType,
	urlCombine,
	UserConfig,
} from 'ihub/common';
import {
	buildURL,
	coreAPI,
	getTenantID,
	handleResponse,
	standardDelete,
	standardGet,
	standardPost,
	validateOkResponse,
} from '../classes/http-helper-const';
import Parameter from '../classes/query-parameter';
import { cacheStaleTime } from '../constants';
import HttpResponseOptions from '../interface/http-response-options';
import QueryClientWrapper from '../utils/create-query-client';

export class ScopedPermissionsConfigHttpService {
	private saveProjectPemissionPath = 'saveProjectPermissions';
	private saveClientPemissionPath = 'SaveClientPermissions';

	async savePermissions(
		data: {
			clientSpaceID?: string;
			permissions: ScopedPermissionsConfig[];
		},
		scopeType: ScopeType,
		options?: {
			success?: (response?: any) => void | Promise<void>;
			error?: (data: any) => void | Promise<void>;
		}
	) {
		const isClient = scopeType === ScopeType.Client;
		const headers: Record<string, string> = {
			[isClient ? scopedClientKey : scopedProjectKey]: data.permissions[0].scopeID,
		};
		const urlPath =
			scopeType === ScopeType.Client ? this.saveClientPemissionPath : this.saveProjectPemissionPath;
		const url = buildURL(ScopedPermissionsConfig.name, [urlPath]);
		const response = await standardPost(url, data, headers);
		await handleResponse(response, 'Permissions Saved', ScopedPermissionsConfig.name, options);
	}

	getScopedUsers(
		scopeId: string,
		scopeType: ScopeType,
		queryParams?: Parameter[]
	): UseQueryOptions<UserConfig[], Error, UserConfig[]> {
		const isClient = scopeType === ScopeType.Client;
		const headers: Record<string, string> = {
			[isClient ? scopedClientKey : scopedProjectKey]: scopeId,
		};
		const scopedURL = isClient ? 'getScopedClientUsers' : 'getScopedProjectUsers';
		return {
			queryKey: [
				getTenantID(),
				ScopedPermissionsConfig.name,
				scopedURL,
				scopeId,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<UserConfig[]> => {
				const url = buildURL(ScopedPermissionsConfig.name, [scopedURL, scopeId], queryParams);
				const res = await standardGet(url, headers);
				await validateOkResponse(res, 'get scoped users', ScopedPermissionsConfig.name);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	getScopedPermissions(
		scopeId: string,
		scopeType: ScopeType
	): UseQueryOptions<ScopedPermissionsConfig[], Error, ScopedPermissionsConfig[]> {
		const isClient = scopeType === ScopeType.Client;
		const headers: Record<string, string> = {
			[isClient ? scopedClientKey : scopedProjectKey]: scopeId,
		};
		const scopedURL = isClient ? 'getScopedClientPermissions' : 'getScopedProjectPermissions';
		return {
			queryKey: [getTenantID(), ScopedPermissionsConfig.name, scopedURL, scopeId],
			queryFn: async (): Promise<ScopedPermissionsConfig[]> => {
				const url = buildURL(ScopedPermissionsConfig.name, [scopedURL, scopeId]);
				const res = await standardGet(url, headers);
				await validateOkResponse(res, 'get scoped permissions', ScopedPermissionsConfig.name);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async transferOwnership(
		transferDetails: ScopedPermissionOwnershipTransfer,
		scopeType: ScopeType,
		options?: HttpResponseOptions
	) {
		const isClient = scopeType === ScopeType.Client;
		const headers: Record<string, string> = {
			[isClient ? scopedClientKey : scopedProjectKey]: transferDetails.scopeID,
		};
		const scopedURL = isClient ? 'transferOwnership/client' : 'transferOwnership/project';

		const url = urlCombine(coreAPI, ScopedPermissionsConfig.name, scopedURL);
		const response = await standardPost(url, transferDetails, headers);
		await handleResponse(response, 'transfer ownership', ScopedPermissionsConfig.name, options);
		await this.invalidateGetScopedPermissions();
	}

	async delete(
		itemId: string,
		scopeId: string,
		scopeType: ScopeType,
		options?: HttpResponseOptions,
		queryParams?: Parameter[]
	): Promise<void> {
		const isClient = scopeType === ScopeType.Client;
		const headers: Record<string, string> = {
			[isClient ? scopedClientKey : scopedProjectKey]: scopeId,
		};

		const scopedURL = isClient ? 'Delete/client' : 'Delete/project';

		const url = buildURL(ScopedPermissionsConfig.name, [scopedURL, itemId], queryParams);
		const response = await standardDelete(url, undefined, headers);
		await handleResponse(response, 'delete', ScopedPermissionsConfig.name, options);
	}

	async invalidateGetScopedUsers(remove = false) {
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), ScopedPermissionsConfig.name, 'getScopedClientUsers'],
			remove
		);
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), ScopedPermissionsConfig.name, 'getScopedProjectUsers'],
			remove
		);
	}

	async invalidateGetScopedPermissions(remove = false) {
		// we invalidate both because It adds the scoped perms to clients if not present
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), ScopedPermissionsConfig.name, 'getScopedClientPermissions'],
			remove
		);
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), ScopedPermissionsConfig.name, 'getScopedProjectPermissions'],
			remove
		);
	}
}
